/**
 * system_settings.repository.ts — `system_settings/{key}` 단순 key/value 저장소.
 * 경로는 settingsDocPath(key, at) 경유 — 그룹 키는 루트, 법인 키는 법인 경로(S1 은 중앙=루트 그대로).
 *
 * 문서 형식: `{ value: string, note?: string, updated_at: Timestamp }`
 *   - value가 JSON 문자열로 보이면 자동 parse
 *   - 콤마 구분 문자열도 자동 split (지원 헬퍼: getStringList)
 *
 * 전용 도큐먼트(category_order 등)는 별도 repository 가 있음 — 자유 라벨 리스트만 이 repo.
 */
import { doc, getDoc, onSnapshot, setDoc, Timestamp } from "firebase/firestore";
import { db } from "./firebase";
import { settingsDocPath, type SettingsAt } from "@/domain/tenantPath";

export type SettingValue = string | number | boolean | null | unknown[] | Record<string, unknown>;

function decodeValue(raw: unknown): SettingValue {
  if (typeof raw !== "string") return (raw as SettingValue) ?? null;
  const t = raw.trim();
  if ((t.startsWith("{") && t.endsWith("}")) || (t.startsWith("[") && t.endsWith("]"))) {
    try {
      return JSON.parse(t) as SettingValue;
    } catch {
      return raw;
    }
  }
  return raw;
}

function asStringList(v: SettingValue): string[] {
  if (Array.isArray(v)) return v.map(String).map((s) => s.trim()).filter(Boolean);
  if (typeof v === "string") return v.split(",").map((s) => s.trim()).filter(Boolean);
  return [];
}

export const systemSettingsRepo = {
  async setJsonValue(at: SettingsAt, key: string, value: Record<string, unknown>, note: string): Promise<void> {
    await setDoc(doc(db, settingsDocPath(key, at)), { value: JSON.stringify(value), note, updated_at: Timestamp.now() }, { merge: true });
  },
  async get(at: SettingsAt, key: string): Promise<SettingValue> {
    const snap = await getDoc(doc(db, settingsDocPath(key, at)));
    if (!snap.exists()) return null;
    return decodeValue(snap.data().value);
  },

  async getStringList(at: SettingsAt, key: string): Promise<string[]> {
    return asStringList(await this.get(at, key));
  },

  watch(at: SettingsAt, key: string, cb: (v: SettingValue) => void, onError?: (e: Error) => void): () => void {
    return onSnapshot(
      doc(db, settingsDocPath(key, at)),
      (snap) => cb(snap.exists() ? decodeValue(snap.data().value) : null),
      onError,
    );
  },

  watchStringList(
    at: SettingsAt,
    key: string,
    cb: (items: string[]) => void,
    onError?: (e: Error) => void,
  ): () => void {
    return this.watch(at, key, (v) => cb(asStringList(v)), onError);
  },

  async setStringList(at: SettingsAt, key: string, items: string[], note?: string): Promise<void> {
    await setDoc(
      doc(db, settingsDocPath(key, at)),
      {
        value: items.join(","),
        note: note ?? "",
        updated_at: Timestamp.now(),
      },
      { merge: true },
    );
  },
};
